/**
 * 画面のスクロール位置を覚えておき、戻る (ブラウザの戻る / 「前の画面へ」) で開き直した時に戻すフック (2026-10-30)。
 *
 * いまのルーター (BrowserRouter) はスクロール位置を覚えないため、Screen のスクロール領域の位置を
 * 履歴の項目ごと (location.key) に sessionStorage へ保存する。
 * - 戻すのは戻る操作 (POP) で開いた時だけ。リンクから開いた時は先頭から表示する
 * - データの読み込みで画面が伸びるのを待ってから戻す (最大 3 秒)。その間にユーザーが操作したらやめる
 */
import { useEffect, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'goenhub.scroll:';
const WAIT_MS = 3000;

function save(key: string, top: number) {
  try {
    sessionStorage.setItem(PREFIX + key, String(Math.round(top)));
  } catch {
    // プライベートモード等で保存できなくても、画面はそのまま動かす
  }
}

function load(key: string): number {
  try {
    const v = Number(sessionStorage.getItem(PREFIX + key));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

export function useScrollMemory(ref: RefObject<HTMLElement>, enabled: boolean) {
  const { key } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    let raf = 0;
    let restoring = false;
    const stopRestoring = () => {
      restoring = false;
      cancelAnimationFrame(raf);
    };

    const target = navigationType === 'POP' ? load(key) : 0;
    if (target > 0) {
      restoring = true;
      const startedAt = performance.now();
      const tick = () => {
        if (!restoring) return;
        const reachable = el.scrollHeight - el.clientHeight >= target;
        if (reachable || performance.now() - startedAt > WAIT_MS) {
          el.scrollTop = target;
          restoring = false;
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    } else if (el.clientHeight > 0) {
      // URL の書き換え (タブ切替など) でも key は変わるので、今の位置をこの key で覚え直す
      // (表示されていない領域は 0 で上書きしないよう除く)
      save(key, el.scrollTop);
    }

    const onScroll = () => {
      if (!restoring) save(key, el.scrollTop);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', stopRestoring, { passive: true });
    el.addEventListener('touchstart', stopRestoring, { passive: true });
    el.addEventListener('pointerdown', stopRestoring);
    return () => {
      stopRestoring();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', stopRestoring);
      el.removeEventListener('touchstart', stopRestoring);
      el.removeEventListener('pointerdown', stopRestoring);
    };
  }, [ref, enabled, key, navigationType]);
}
